/**
 * Health — saúde da conexão Cloud API.
 * Token expiry, last_webhook_at, alertas históricos do canal.
 */

import React from 'react';
import {
  IconKey,
  IconActivityHeartbeat,
  IconHistory,
  IconAlertTriangle,
} from '@tabler/icons-react';
import useCloudApiStatus from '../useStatus';
import { useChannelAlerts } from '../../../../contexts/ChannelAlertsContext';
import {
  SEVERITY_THEME,
  EVENT_LABELS,
  EVENT_ICONS,
  effectiveSeverity,
} from '../../../../components/alerts/alertConfig';

function formatRelativeTime(date) {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `${mins} min atrás`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} dia(s) atrás`;
  return d.toLocaleDateString('pt-BR');
}

function daysFromNow(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

const Health = ({ agentId }) => {
  const status = useCloudApiStatus(agentId);
  const { alerts: history } = useChannelAlerts({ channelKey: 'cloud_api' });
  const data = status.raw || {};

  const tokenExpiresAt = data.token_expires_at;
  const tokenDaysLeft = daysFromNow(tokenExpiresAt);
  const tokenIsPermanent = !tokenExpiresAt;
  const tokenExpired = tokenDaysLeft !== null && tokenDaysLeft <= 0;
  const tokenWarning = tokenDaysLeft !== null && tokenDaysLeft <= 7 && !tokenExpired;
  const tokenError = data.token_validation_error;

  const lastWebhook = data.last_webhook_at;
  const lastWebhookHours = lastWebhook
    ? Math.floor((Date.now() - new Date(lastWebhook).getTime()) / (1000 * 60 * 60))
    : null;
  const webhookHealthy =
    lastWebhookHours === null ? false : lastWebhookHours < 24;

  return (
    <div>
      {/* Token */}
      <div className="cloudapi-section">
        <h4 className="cloudapi-section__title">
          <IconKey size={16} />
          Status do Token
        </h4>
        <div className="cloudapi-section__row">
          <span className="cloudapi-section__row-label">Validade</span>
          <span className="cloudapi-section__row-value">
            {tokenIsPermanent && (
              <span style={{ color: '#16a34a' }}>Permanente (Nunca expira)</span>
            )}
            {tokenExpired && (
              <span style={{ color: '#dc2626' }}>EXPIRADO há {Math.abs(tokenDaysLeft)} dia(s)</span>
            )}
            {!tokenIsPermanent && !tokenExpired && (
              <span style={{ color: tokenWarning ? '#d97706' : '#111827' }}>
                Expira em {tokenDaysLeft} dia(s)
              </span>
            )}
          </span>
        </div>
        {data.token_validated_at && (
          <div className="cloudapi-section__row">
            <span className="cloudapi-section__row-label">Última validação</span>
            <span className="cloudapi-section__row-value">
              {formatRelativeTime(data.token_validated_at)}
            </span>
          </div>
        )}
        {tokenError && (
          <div
            style={{
              marginTop: 8,
              padding: 10,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              color: '#991b1b',
              fontSize: 12,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}
          >
            <IconAlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{tokenError}</span>
          </div>
        )}
      </div>

      {/* Webhook health */}
      <div className="cloudapi-section">
        <h4 className="cloudapi-section__title">
          <IconActivityHeartbeat size={16} />
          Saúde do Webhook
        </h4>
        <div className="cloudapi-section__row">
          <span className="cloudapi-section__row-label">Última atividade</span>
          <span className="cloudapi-section__row-value">
            <span
              style={{
                color: webhookHealthy ? '#16a34a' : lastWebhook ? '#d97706' : '#9ca3af',
              }}
            >
              {lastWebhook ? formatRelativeTime(lastWebhook) : 'Nunca'}
            </span>
          </span>
        </div>
        {!webhookHealthy && (
          <div
            style={{
              marginTop: 8,
              padding: 10,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 6,
              color: '#92400e',
              fontSize: 12,
            }}
          >
            {lastWebhook
              ? 'Sem mensagens há mais de 24h. Pode indicar problema de subscription.'
              : 'Webhook nunca recebeu mensagem. Confirme subscription no Meta Business.'}
          </div>
        )}
      </div>

      {/* Alert history */}
      <div className="cloudapi-section">
        <h4 className="cloudapi-section__title">
          <IconHistory size={16} />
          Histórico de Alertas ({history.length})
        </h4>
        {history.length === 0 ? (
          <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
            Nenhum alerta registrado.
          </p>
        ) : (
          <div style={{ maxHeight: 320, overflow: 'auto' }}>
            {history.slice(0, 20).map((alert) => {
              const severity = effectiveSeverity(alert);
              const theme = SEVERITY_THEME[severity];
              const Icon = EVENT_ICONS[alert.event];
              return (
                <div
                  key={alert.id}
                  style={{
                    display: 'flex',
                    gap: 8,
                    padding: '8px 0',
                    borderBottom: '1px solid #f3f4f6',
                    fontSize: 12,
                  }}
                >
                  {Icon && <Icon size={16} style={{ color: theme.iconColor, flexShrink: 0, marginTop: 2 }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: theme.color, marginBottom: 2 }}>
                      {EVENT_LABELS[alert.event] || alert.event}
                      <span style={{ marginLeft: 8, fontWeight: 400, color: '#9ca3af' }}>
                        · {formatRelativeTime(new Date(alert.createdAt))}
                      </span>
                    </div>
                    <div style={{ color: '#4b5563' }}>{alert.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Health;
